import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Dimmer, Grid, Loader } from "semantic-ui-react";

import { useGetCompanyName, useGetCompanyToken } from "../../api/company";
import { usePostUserDetails, usePostUserLogin } from "../../api/userDetails";
import HiddenPasswordIcon from "../../assets/commonIcon/HiddenPasswordIcon";
import VisiblePasswordIcon from "../../assets/commonIcon/VisiblePasswordIcon";
import { CustomButton, InputText } from "../../components";
import { notificationSetupService } from "../../config/firebase";
import { errorView } from "../../helpers/ErrorHandler";
import { useAuthContext } from "../../hooks/useAuthContext";
import { setAccessToken } from "../../utils/authTokenStore";
import {
  consumeReturnPath,
  removeTokens,
  saveCompanyTenantId,
  saveTenantId,
  saveUserTenantId
} from "../../utils/cacheStorage";
import { checkAvailableUserCompanyId } from "../../utils/utils";

import "./Login.scss";
import { LOGIN, SET_COMPANY, SET_COMPANY_ID } from "./loginTypes";

const Login = () => {
  const navigate = useNavigate();
  const { dispatch }: any = useAuthContext();

  const [emailInput, setEmailInput] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm();

  const { data: loginResponseData, isLoading: isLoggingIn, mutate: loginUser } = usePostUserLogin();

  const {
    data: userProfile,
    isLoading: isLoadingProfile,
    mutate: fetchUserProfile
  } = usePostUserDetails();

  const {
    data: companyDetails,
    status: companyFetchStatus,
    fetchStatus: companyFetchState
  } = useGetCompanyName(userProfile?.tenantId);

  const { isLoading: isFetchingCompanyToken, mutate: fetchCompanyToken } = useGetCompanyToken();

  useEffect(() => {
    if (companyFetchStatus === "success") {
      handleCompanyInfoLoad();
    }
  }, [companyFetchStatus]);

  const handleLoginSuccess = (response: any) => {
    removeTokens();
    setAccessToken(response.token);
    handleUserProfileLoad(response);
  };

  const handleUserProfileLoad = (loginData: any) => {
    if (loginData?.isPasswordResetRequired) {
      navigateToPasswordReset();
    } else {
      fetchUserProfile("");
    }
  };

  const navigateToPasswordReset = () => {
    navigate("/password-reset", {
      state: {
        isPasswordResetRequired: true,
        email: emailInput
      }
    });
  };

  const handleCompanyInfoLoad = () => {
    const {
      id: companyId,
      status: isValidCompany,
      name: companyName,
      tenantId: companyTenantId
    } = checkAvailableUserCompanyId(companyDetails, userProfile);

    if (!isValidCompany) {
      errorView("User has no company access.");
      removeTokens();
      return;
    }

    fetchCompanyToken(companyId, {
      onSuccess: tokenData => {
        persistCompanyTokens(tokenData, companyName, companyId, companyTenantId);
      }
    });
  };

  const storeCompanyAuthTokens = (
    tokenData: any,
    tenantId: string | "",
    companyTenantId: string
  ) => {
    setAccessToken(tokenData?.token ?? null);
    saveTenantId(tenantId);
    saveCompanyTenantId(companyTenantId);
    saveUserTenantId(tenantId);
    if (userProfile) notificationSetupService.setup(userProfile, () => {});
  };

  const initializeUserSession = (
    userProfile: any,
    companyName: string,
    companyId: string,
    dispatch: React.Dispatch<any>
  ) => {
    notificationSetupService.setup(userProfile, () => {});
    dispatch({ type: SET_COMPANY, payload: companyName });
    dispatch({ type: SET_COMPANY_ID, payload: companyId });
    dispatch({ type: LOGIN, payload: userProfile });
    navigate(consumeReturnPath() ?? "/", {
      state: { isPasswordResetRequired: false, email: emailInput }
    });
  };

  const persistCompanyTokens = (
    tokenData: any,
    companyName: string,
    companyId: string,
    companyTenantId: string
  ) => {
    const tenantId = userProfile?.tenantId ?? null;
    storeCompanyAuthTokens(tokenData, tenantId as string, companyTenantId);
    if (!loginResponseData?.isPasswordResetRequired && userProfile) {
      initializeUserSession(userProfile, companyName, companyId, dispatch);
    } else {
      navigateToPasswordReset();
    }
  };

  const handleFormSubmit = (formData: any) => {
    setEmailInput(formData.email);
    loginUser(formData, {
      onSuccess: handleLoginSuccess
    });
  };

  const isLoadingApp =
    isLoggingIn ||
    isLoadingProfile ||
    isFetchingCompanyToken ||
    (companyFetchStatus === "loading" && companyFetchState === "fetching");

  if (isLoadingApp) {
    return (
      <Dimmer active>
        <Loader content="Loading" />
      </Dimmer>
    );
  }

  return (
    <>
      <form className="mainContaa" onSubmit={handleSubmit(handleFormSubmit)}>
        <Grid className="backgroundLoginMain">
          <Grid.Column computer={16} tablet={16} mobile={16}>
            <span className="welcomeTxt">Welcome!</span>
          </Grid.Column>
          <Grid.Column className="pt-0" computer={16} tablet={16} mobile={16}>
            <span className="secondaryTxt">Please sign in to continue.</span>
          </Grid.Column>
          <Grid.Column className="pt-0" computer={16} tablet={16} mobile={16}>
            <InputText
              register={register}
              errors={errors.email}
              required={true}
              labelName={"Username*"}
              placeholder="Enter Username"
              name="email"
              errorMessage="Please enter email"
            />
          </Grid.Column>
          <Grid.Column className="pt-0 pb-0" computer={16} tablet={16} mobile={16}>
            <InputText
              register={register}
              errors={errors.password}
              required={true}
              labelName={"Password*"}
              placeholder="Enter Password"
              name="password"
              type={isPasswordVisible ? "text" : "password"}
              errorMessage="Please enter password"
              rightContentEnable={true}
              rightContent={
                isPasswordVisible ? (
                  <HiddenPasswordIcon
                    onClick={() => setIsPasswordVisible(false)}
                    className="passwordIcon"
                  />
                ) : (
                  <VisiblePasswordIcon
                    onClick={() => setIsPasswordVisible(true)}
                    className="passwordIcon"
                  />
                )
              }
            />
          </Grid.Column>
          <Grid.Column className="pt-1" computer={16} tablet={16} mobile={16}>
            <a className="forgotTxt" onClick={() => navigate("/password-forgot")}>
              Forgot Your Password?
            </a>
          </Grid.Column>
          <Grid.Column className="pt-0" computer={16} tablet={16} mobile={16}>
            <CustomButton
              theme="green"
              title={"Login"}
              customButtonStyle={"customBtnn"}
              type={"submit"}
            />
          </Grid.Column>
        </Grid>
      </form>
    </>
  );
};

export default Login;
